import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import collectionsService from "../../services/collectionsService";
import { Form, Input, Button, Space, DatePicker, Select, Row, Col, Card } from "antd";
import {
  InfoCircleOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import videoService from "../../services/videoService";
import tvShowService from "../../services/tvShowService";
import masterService from "../../services/masterService";
import announcementService from "../../services/announcementService";
import ToggleSwitch from "../common/ToggleSwitch";

const { RangePicker } = DatePicker;

const extractDatePart = (value) => {
  if (!value) return null;
  const s = dayjs.isDayjs(value)
    ? value.format("YYYY-MM-DD")
    : String(value instanceof Date ? value.toISOString() : value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};
const formatStartDateTime = (value) => {
  const d = extractDatePart(value);
  return d ? `${d}T00:00:00Z` : null;
};
const formatEndDateTime = (value) => {
  const d = extractDatePart(value);
  return d ? `${d}T23:59:59Z` : null;
};

const CollectionFiltersForm = ({
  initialValues,
  loading,
  onSubmit,
  onBack,
  enums,
  collectionId,
  sortedItem,
  copyPinned
}) => {
  const [form] = Form.useForm();

  // Helpers (moved before state to avoid initialization issues)
  const csvToArray = (csv) =>
    typeof csv === "string" && csv.trim().length
      ? csv
          .split(",")
          .map((s) => Number(String(s).trim()))
          .filter((n) => !Number.isNaN(n))
      : [];

  const csvToStringArray = (csv) =>
    typeof csv === "string" && csv.trim().length
      ? csv
          .split(",")
          .map((s) => String(s).trim())
          .filter((s) => s.length > 0)
      : [];

  // Options state
  const [catOptions, setCatOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [ctypeOptions, setCtypeOptions] = useState([]);
  const [langOptions, setLangOptions] = useState([]);
  const [ratingOptions, setRatingOptions] = useState([]);
  const [genreOptions, setGenreOptions] = useState([]);
  const [subGenres, setSubGenres] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [tvOptions, setTvOptions] = useState([]);
  const [movieOptions, setMovieOptions] = useState([]);
  const [announcementOptions, setAnnouncementOptions] = useState([]);

  // Which announcement picker to show, watched rather than held in state: the
  // type is set both by the admin and by the initialValues effect below, and a
  // second copy of it would go stale on the second of those.
  const collectionType = Form.useWatch("collection_types", form);

  // Form state for conditional rendering
  const [bannerType, setBannerType] = useState(
    initialValues?.banner_type || "Show"
  );
  const [movieTypeBy, setMovieTypeBy] = useState(
    initialValues?.movie_type_by || "None"
  );
  const [bannerShowType, setBannerShowType] = useState(
    csvToStringArray(initialValues?.banner_show_type || "")
  );
  const [recentBy, setRecentBy] = useState(initialValues?.recent_by || "None");
  const [tagIds, setTagIds] = useState([]);
  const [categoryList, setCategoryList] = useState([]);

  const toOption = (value, label) => ({ value, label });

  // Helper: compute start/end for upcoming periods using ISO week (Mon-Sun)
  const computeUpcomingRange = (period) => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun..6=Sat
    const offsetToMonday = (day + 6) % 7; // Mon=0
    const thisMon = new Date(now);
    thisMon.setHours(0, 0, 0, 0);
    thisMon.setDate(thisMon.getDate() - offsetToMonday);
    const thisSun = new Date(thisMon);
    thisSun.setDate(thisMon.getDate() + 6);
    thisSun.setHours(23, 59, 59, 999);

    const nextMon = new Date(thisMon);
    nextMon.setDate(thisMon.getDate() + 7);
    nextMon.setHours(0, 0, 0, 0);
    const nextSun = new Date(nextMon);
    nextSun.setDate(nextMon.getDate() + 6);
    nextSun.setHours(23, 59, 59, 999);

    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    if (period === "ThisWeek") return [dayjs(thisMon), dayjs(thisSun)];
    if (period === "NextWeek") return [dayjs(nextMon), dayjs(nextSun)];
    if (period === "LaterThisMonth") {
      const start = new Date(nextSun);
      start.setDate(nextSun.getDate() + 1);
      start.setHours(0, 0, 0, 0);
      // If start is after monthEnd, no valid remaining days in this month
      if (start > monthEnd) return [undefined, undefined];
      return [dayjs(start), dayjs(monthEnd)];
    }
    return [undefined, undefined];
  };

  // Enum options
  const movieTypeByOptions = [
    { value: "None", label: "None" },
    { value: "ReleaseDate", label: "Release Date" },
    { value: "ExpiryDate", label: "Expiry Date" },
  ];

  const bannerTypeOptions = [
    { value: "Show", label: "Show" },
    { value: "Generic", label: "Generic" },
    { value: "Promotion", label: "Promotion" },
  ];

  const commonShowTypeOptions = [
    { value: "movie", label: "Movie" },
    { value: "series", label: "Series" },
    { value: "short", label: "Short" },
  ];

  // Upcoming range options (visible when recent_by === 'Upcoming')
  const upcomingRangeOptions = [
    { value: "None", label: "None" },
    { value: "ThisWeek", label: "This week" },
    { value: "NextWeek", label: "Next week" },
    { value: "LaterThisMonth", label: "Later this month" },
  ];

  // New: Collection Types that can compose a collection
  const collectionTypeOptions = [
    { value: "Movie", label: "Movie" },
    { value: "Series", label: "Series" },
    { value: "Movie_Series", label: "Movie & Series" },
    { value: "Banner", label: "Banner" },
    { value: "Announcement", label: "Announcement" },
    { value: "Spotlight", label: "Spotlight" },
    { value: "Collection", label: "Collection" },
    { value: "Language", label: "Language" },
    { value: "Genre", label: "Genre" },
    { value: "Tag", label: "Tag" },
  ];

  // Initialize form values for multi-selects from CSV strings OR arrays from API
  useEffect(() => {
    const iv = initialValues || {};

    const extractIds = (arr, keys = ["id", "value"]) => {
      if (!Array.isArray(arr)) return [];
      return arr
        .map((o) => {
          if (typeof o === "number") return o;
          if (!o || typeof o !== "object") return undefined;
          for (const k of keys) {
            if (o[k] != null) return Number(o[k]);
          }
          // common fk_* keys fallbacks
          const fkKey = Object.keys(o).find(
            (k) => String(k).startsWith("fk_") && typeof o[k] !== "object"
          );
          return fkKey ? Number(o[fkKey]) : undefined;
        })
        .filter((n) => typeof n === "number" && !Number.isNaN(n));
    };

    // Prefer CSV when present, else derive from arrays supplied by API
    const categoryIds = csvToArray(iv.category_ids).length
      ? csvToArray(iv.category_ids)
      : extractIds(iv.categories, ["category_id", "vc_id", "value"]);

    const categoryList = csvToArray(iv.mscategory_list).length
      ? csvToArray(iv.mscategory_list)
      : extractIds(iv.mscategory_list, ["category_list","vc_id", "value"]);
    console.log("categoryList", categoryList);

    const ctypeIds = csvToArray(iv.content_type_ids).length
      ? csvToArray(iv.content_type_ids)
      : extractIds(iv.selectedContentTypes, [
          "content_type_id",
          "ct_id",
          "value",
        ]);
    const langIds = csvToArray(iv.language_ids).length
      ? csvToArray(iv.language_ids)
      : extractIds(iv.selectedLanguages, ["lang_id", "value"]);
    const ratingIds = csvToArray(iv.rating_ids).length
      ? csvToArray(iv.rating_ids)
      : extractIds(iv.selectedRatings, ["rating_id", "vr_id", "value"]);
    const genreIds = csvToArray(iv.genre_ids).length
      ? csvToArray(iv.genre_ids)
      : extractIds(iv.selectedGenres, ["genre_id", "value"]);
    const subGenreIds = csvToArray(iv.sub_genre_ids).length
      ? csvToArray(iv.sub_genre_ids)
      : extractIds(iv.sub_genre_ids, ["genre_id", "value"]);
    const tagIds = csvToArray(iv.tag_ids).length
      ? csvToArray(iv.tag_ids)
      : extractIds(iv.selectedTags, ["tag_id", "value"]);
    const countryIds = csvToArray(iv.country_ids).length
      ? csvToArray(iv.country_ids)
      : extractIds(iv.selectedCountries, ["country_id", "value"]);
    const tvIds = csvToArray(iv.tv_series_show_ids).length
      ? csvToArray(iv.tv_series_show_ids)
      : extractIds(iv.selectedTvSeries, ["tv_show_id", "value"]);
    const movieIds = csvToArray(iv.movie_ids).length
      ? csvToArray(iv.movie_ids)
      : extractIds(iv.selectedMovies, ["video_show_id", "value"]);
    // Empty is meaningful here rather than missing: it means the rail takes every
    // active announcement, newest first, which is what it did before this picker
    // existed.
    const announcementIds = csvToArray(iv.announcement_ids).length
      ? csvToArray(iv.announcement_ids)
      : extractIds(iv.selectedAnnouncements, ["m_announcement_id", "value"]);

    const derivedBannerShow = csvToStringArray(iv.banner_show_type);
    if ((iv.banner_type || "Show") === "Show") {
      if (derivedBannerShow.length === 0) {
        if (tvIds.length > 0) derivedBannerShow.push("series");
        if (movieIds.length > 0) derivedBannerShow.push("movie");
      }
    }

    form.setFieldsValue({
      category_ids: categoryIds,
      category_list: iv.mscategory_list,
      mscategory_list: iv.mscategory_list,
      tag_ids: tagIds,
      content_type_ids: ctypeIds,
      language_ids: langIds,
      rating_ids: ratingIds,
      genre_ids: genreIds,
      sub_genre_ids: subGenreIds,
      country_ids: countryIds,
      tv_series_show_ids: tvIds,
      movie_ids: movieIds,
      announcement_ids: announcementIds,
      recent_by: iv.recent_by || "None",
      upcoming_period: iv.upcoming_period,
      is_auth_required:
        typeof iv.is_auth_required === "boolean" ? iv.is_auth_required : false,
      collection_types: (() => {
        const arr = csvToStringArray(iv.collection_types);
        // If API provided lowercase values previously, normalize to capitalized to match options
        const pick = arr && arr.length ? arr[0] : iv.collection_types;
        switch ((pick || "").toString()) {
          case "Movie":
            return "Movie";
          case "Series":
            return "Series";
          case "Movie_Series":
            return "Movie_Series";
          case "Banner":
            return "Banner";
          case "Announcement":
            return "Announcement";
          case "Spotlight":
            return "Spotlight";
          case "Collection":
            return "Collection";
          case "Language":
            return "Language";
          case "Genre":
            return "Genre";
          default:
            return "Collection";
        }
      })(),
      is_subscription_required:
        typeof iv.is_subscription_required === "boolean"
          ? iv.is_subscription_required
          : false,
      banner_show_type: derivedBannerShow,
     
    });
    // Update state for conditional rendering
    setBannerType(iv.banner_type || "Show");
    setMovieTypeBy(iv.movie_type_by || "None");
    setBannerShowType(derivedBannerShow);
    setRecentBy(iv.recent_by || "None");
    setTagIds(tagIds);
    setCategoryList(categoryList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Backfill dateRange when Recent By is 'Upcoming' and an upcoming_period is selected
  useEffect(() => {
    try {
      if (recentBy !== "Upcoming") return;
      const sel = form.getFieldValue("upcoming_period");
      if (!sel) return;
      const [s, e] = computeUpcomingRange(sel);
      if (s && e) {
        form.setFieldsValue({ dateRange: [s, e] });
      }
    } catch {}
  }, [recentBy]);

  // Fetchers
  const fetchCategories = async (searchText = "") => {
    const res = await videoService.getAllVideoCategory({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setCatOptions(list.map((r) => toOption(r.vc_id, r.vc_name)));
  };
  const fetchTags = async (searchText = "") => {
    const res = await masterService.getTagList({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setTagOptions(list.map((r) => toOption(r.tag_id, r.tag_name)));
  };
  const fetchContentTypes = async (searchText = "") => {
    const res = await masterService.getContentTypes({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setCtypeOptions(list.map((r) => toOption(r.ct_id, r.ct_name)));
  };
  const fetchLanguages = async (searchText = "") => {
    const res = await masterService.getVideoLanguages({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setLangOptions(list.map((r) => toOption(r.lang_id, r.lang_name)));
  };
  const fetchRatings = async (searchText = "") => {
    const res = await videoService.getAllVideoRatings({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setRatingOptions(list.map((r) => toOption(r.vr_id, r.rating_name)));
  };
  const fetchGenres = async (searchText = "") => {
    const res = await masterService.getGenres({
      perPage: 50,
      currentPage: 1,
      searchText,
      status: "all",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setGenreOptions(list.map((r) => toOption(r.genre_id, r.genre_name)));
  };
  const fetchCountries = async (searchText = "") => {
    const res = await masterService.getCountries({
      perPage: 50,
      currentPage: 1,
      searchText,
      status: "all",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setCountryOptions(list.map((r) => toOption(r.country_id, r.country_name)));
  };
  const fetchTV = async (searchText = "") => {
    const res = await tvShowService.getAllTvShow({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setTvOptions(list.map((r) => toOption(r.tv_show_id, r.tv_show_name)));
  };
  const fetchMovies = async (searchText = "") => {
    const res = await videoService.getAllVideoShow({
      perPage: 50,
      currentPage: 1,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setMovieOptions(
      list.map((r) =>
        toOption(r.video_show_id, r.video_show_title || r.video_show_name)
      )
    );
  };

  // Only ACTIVE announcements are offered — the read side serves active ones
  // only, so an inactive pick would save and then show nothing.
  const fetchAnnouncements = async (searchText = "") => {
    const res = await announcementService.getAnnouncements({
      perPage: 50,
      currentPage: 1,
      searchText,
      status: "Y",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    setAnnouncementOptions(
      list.map((r) =>
        toOption(
          r.m_announcement_id,
          r.announcement_title || r.announcement_name || `Announcement #${r.m_announcement_id}`
        )
      )
    );
  };

  const getSubGenres = async () => {
    const selectedGenres = form.getFieldValue("genre_ids");
    console.log("selectedGenres123", selectedGenres);
    const res = await masterService.getSubGenres({
      isAll: true,
      genre_id: selectedGenres?.join(",") || "",
    });

    const data = res?.data?.data?.data || res?.data?.data || res?.data || [];
    const transformed = data.map((item) => ({
      label: item.genre_name,
      value: item.genre_id,
    }));
    console.log("res123", res);
    setSubGenres(transformed);
  };
  const fetchMasterCategories = async () => {
    try {
      const res = await masterService.getMasterCategory({ 
        perPage: 1000, 
        currentPage: 1, 
        searchText: '',
        isAll: true,
        status: 'Y'
      });
      const list = res?.data?.data?.data || res?.data?.data || res?.data || [];
      setCatOptions(list.map((r) => toOption(r.vc_id, r.vc_name)));
    } catch (error) {
      console.error('Error fetching master categories:', error);
    }
  };

  useEffect(() => {
    // Prime dropdowns
    // fetchCategories();
    fetchTags();
    fetchContentTypes();
    fetchLanguages();
    fetchRatings();
    fetchGenres();
    fetchCountries();
    // Load initial TV shows and movies for better UX
    fetchTV();
    fetchMovies();
    fetchAnnouncements();
    getSubGenres();
    fetchMasterCategories()
  }, []);

  const normalizeAndSubmit = (values) => {
    const arrToCsv = (v) => (Array.isArray(v) ? v.join(",") : v || null);
    const toValueArray = (v) =>
      Array.isArray(v) ? v.map((id) => ({ value: id })) : [];

    // Build both CSV (legacy) and array-of-objects (value) payloads
    // Map UI label to API code for collection type
    const mapTypeLabelToCode = (label) => {
      switch ((label || "").toString()) {
        case "Movie":
          return "Movie";
        case "Series":
          return "Series";
        case "Movie_Series":
          return "Movie_Series";
        case "Banner":
          return "Banner";
        case "Announcement":
          return "Announcement";
        case "Spotlight":
          return "Spotlight";
        case "Collection":
          return "Collection";
        case "Language":
          return "Language";
        case "Genre":
          return "Genre";
        default:
          return null;
      }
    };

    const dateRange = values.dateRange;
    const payload = {
      ...values,
      is_subscription_required:
        typeof values.is_subscription_required === "boolean"
          ? values.is_subscription_required
          : values.is_subscription_required === "true",
      from_date: formatStartDateTime(dateRange?.[0]),
      to_date: formatEndDateTime(dateRange?.[1]),
      dateRange: undefined,
      // CSV strings (existing API compatibility)
      category_ids: arrToCsv(values.category_ids),
      tag_ids: arrToCsv(values.tag_ids),
      content_type_ids: arrToCsv(values.content_type_ids),
      language_ids: arrToCsv(values.language_ids),
      rating_ids: arrToCsv(values.rating_ids),
      genre_ids: arrToCsv(values.genre_ids),
      country_ids: arrToCsv(values.country_ids),
      tv_series_show_ids: arrToCsv(values.tv_series_show_ids),
      movie_ids: arrToCsv(values.movie_ids),
      announcement_ids: arrToCsv(values.announcement_ids),
      collection_types: mapTypeLabelToCode(values.collection_types),

      // Array versions expected by backend mappers (e.g., toIdString on fk_* or value)
      categories: toValueArray(values.category_ids),
      mscategory_list: values.category_list,
      tags: toValueArray(values.tag_ids),
      selectedContentTypes: toValueArray(values.content_type_ids),
      selectedLanguages: toValueArray(values.language_ids),
      selectedRatings: toValueArray(values.rating_ids),
      selectedGenres: toValueArray(values.genre_ids),
      selectedTags: toValueArray(values.tag_ids),
      selectedCountries: toValueArray(values.country_ids),
      selectedTvSeries: toValueArray(values.tv_series_show_ids),
      selectedMovies: toValueArray(values.movie_ids),
      selectedAnnouncements: toValueArray(values.announcement_ids),
      selectedCollectionTypes: (() => {
        const code = mapTypeLabelToCode(values.collection_types);
        return code ? [{ value: code }] : [];
      })(),

      // New fields
      movie_type_by: values.movie_type_by || "None",
      banner_type: values.banner_type || "Show",
      banner_show_type: arrToCsv(values.banner_show_type),
      recent_by: values.recent_by || "None",
      // New upcoming filter (only when recent_by is 'Upcoming'); map 'none' to null
      upcoming_period:
        values.recent_by === "Upcoming"
          ? values.upcoming_period && values.upcoming_period !== "none"
            ? values.upcoming_period
            : null
          : null,
    };
    console.log("FilterxPayload:", payload);
    onSubmit && onSubmit(payload);
  };

  const handleGenreChange = (value) => {
    console.log("value123", value);
    getSubGenres();
    form.setFieldsValue({
      sub_genre_ids: [],
    });
  };

const pinnedIds = new Set(copyPinned.map(p => p.video_show_id));

const filtered = sortedItem.filter(item =>
  !pinnedIds.has(item.video_show_id)
);
  const handleCategoryOrTagsChange = async (source, value) => {
    const newValue = value || [];
    const isCategory = source === "category";
  
    if (isCategory) {
      setCategoryList(newValue);
      if (newValue.length > 0) {
        form.setFieldsValue({ tag_ids: [] });
        setTagIds([]);
      } else {
        const filterdata = filtered?.length > 0 ? filtered : [];
        await collectionsService.upsertPinnedItems(collectionId, filterdata);
      }
    } else {
      setTagIds(newValue);
      if (newValue.length > 0) {
        form.setFieldsValue({ category_list: [] });
        setCategoryList([]);
      } else {
        const filterdata = filtered?.length > 0 ? filtered : [];
        await collectionsService.upsertPinnedItems(collectionId, filterdata);
      }
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onFinish={normalizeAndSubmit}
      style={{ maxWidth: 1100 }}
    >
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={6}>
            <Form.Item
              name="collection_types"
              label="Collection Type"
              rules={[
                { required: true, message: "Please select collection type" },
              ]}
              tooltip={{
                title: "Type of items allowed in this collection",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                allowClear
                showSearch
                options={collectionTypeOptions}
                placeholder="Select collection type"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="page_limit"
              label="Page Limit"
              tooltip={{
                title:
                  "Number of items to display in the collection carousel/grid",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input type="number" min={1} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="is_subscription_required"
              label="Is Subscription Required"
              rules={[
                {
                  required: true,
                  message: "Please select subscription requirement",
                },
              ]}
            >
              <Select
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
                placeholder="Select option"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={6}>
            <Form.Item name="sort_by" label="Sort By">
              <Select options={enums?.sortBy} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="recent_by" label="Recent By">
              <Select
                options={enums?.recentBy}
                onChange={(v) => {
                  setRecentBy(v);
                  if (v !== "Upcoming") {
                    form.setFieldsValue({ upcoming_period: undefined });
                  }
                  // If switched to Upcoming and a period was already selected, backfill dateRange
                  if (v === "Upcoming") {
                    const sel = form.getFieldValue("upcoming_period");
                    if (sel) {
                      const [s, e] = computeUpcomingRange(sel);
                      form.setFieldsValue({
                        dateRange: s && e ? [s, e] : undefined,
                      });
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
          {recentBy === "Upcoming" && (
            <Col xs={24} md={6}>
              <Form.Item name="upcoming_period" label="Upcoming Range">
                <Select
                  options={upcomingRangeOptions}
                  placeholder="Select range"
                  onChange={(val) => {
                    if (!val || val === "none") {
                      form.setFieldsValue({ dateRange: undefined });
                      return;
                    }
                    const [s, e] = computeUpcomingRange(val);
                    if (s && e) {
                      form.setFieldsValue({ dateRange: [s, e] });
                    } else {
                      form.setFieldsValue({ dateRange: undefined });
                    }
                  }}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={6}>
            <Form.Item
              name="movie_type_by"
              label="Movie Type By"
              tooltip={{
                title:
                  "How to apply date filtering - by release date or expiry date",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                options={movieTypeByOptions}
                onChange={(value) => setMovieTypeBy(value)}
                placeholder="Select movie type filter"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="dateRange"
              label={
                movieTypeBy === "ReleaseDate"
                  ? "Release Date Range"
                  : movieTypeBy === "ExpiryDate"
                  ? "Expiry Date Range"
                  : "Date Range"
              }
              tooltip={{
                title:
                  movieTypeBy === "ReleaseDate"
                    ? "Filter content by release date range"
                    : movieTypeBy === "ExpiryDate"
                    ? "Filter content by expiry date range"
                    : "Filter content by date range",
                icon: <InfoCircleOutlined />,
              }}
            >
              <RangePicker
                style={{ width: "100%" }}
                onChange={(dates) => {
                  // Auto-select upcoming_period only when Recent By is 'Upcoming'
                  try {
                    if (recentBy !== "Upcoming") return;
                    const [start, end] = Array.isArray(dates) ? dates : [];
                    if (!start || !end) {
                      form.setFieldsValue({ upcoming_period: undefined });
                      return;
                    }
                    const startDate = start.toDate();
                    const endDate = end.toDate();

                    const today = new Date();
                    // Get Monday as start of week (ISO week: Mon-Sun)
                    const day = today.getDay(); // 0=Sun..6=Sat
                    const offsetToMonday = (day + 6) % 7; // Mon=0
                    const thisWeekStart = new Date(today);
                    thisWeekStart.setHours(0, 0, 0, 0);
                    thisWeekStart.setDate(
                      thisWeekStart.getDate() - offsetToMonday
                    );
                    const thisWeekEnd = new Date(thisWeekStart);
                    thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
                    thisWeekEnd.setHours(23, 59, 59, 999);

                    const nextWeekStart = new Date(thisWeekStart);
                    nextWeekStart.setDate(thisWeekStart.getDate() + 7);
                    const nextWeekEnd = new Date(nextWeekStart);
                    nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
                    nextWeekEnd.setHours(23, 59, 59, 999);

                    // Later this month: after next week but still within current month
                    const monthEnd = new Date(
                      today.getFullYear(),
                      today.getMonth() + 1,
                      0
                    );
                    monthEnd.setHours(23, 59, 59, 999);

                    const rangeWithin = (s, e, a, b) => s >= a && e <= b;

                    let period;
                    if (
                      rangeWithin(
                        startDate,
                        endDate,
                        thisWeekStart,
                        thisWeekEnd
                      )
                    ) {
                      period = "ThisWeek";
                    } else if (
                      rangeWithin(
                        startDate,
                        endDate,
                        nextWeekStart,
                        nextWeekEnd
                      )
                    ) {
                      period = "NextWeek";
                    } else if (
                      endDate > nextWeekEnd &&
                      endDate <= monthEnd &&
                      startDate >= nextWeekEnd
                    ) {
                      period = "LaterThisMonth";
                    } else {
                      period = undefined; // not matching a canned period
                    }
                    form.setFieldsValue({ upcoming_period: period });
                  } catch (e) {
                    // fail-safe: do not block UI
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="banner_type"
              label="Banner Type"
              tooltip={{
                title: "Type of banner - Show, Generic, or Promotion",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                options={bannerTypeOptions}
                onChange={(value) => {
                  setBannerType(value);
                  console.log("Banner Type changed:", value);
                  // Reset banner_show_type when banner_type changes
                  if (value !== "Show") {
                    form.setFieldsValue({ banner_show_type: undefined });
                    setBannerShowType([]);
                  }
                  // Reset movie and TV show selections when banner type changes
                  form.setFieldsValue({
                    movie_ids: [],
                    tv_series_show_ids: [],
                  });
                }}
                placeholder="Select banner type"
              />
            </Form.Item>
          </Col>
          {bannerType === "Show" && (
            <Col xs={24} md={6}>
              <Form.Item
                name="banner_show_type"
                label="Banner Show Type"
                tooltip={{
                  title:
                    "Type of show content for banner (multiple selection allowed)",
                  icon: <InfoCircleOutlined />,
                }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  options={commonShowTypeOptions}
                  placeholder="Select show types"
                  onChange={(value) => {
                    const newShowTypes = value || [];
                    setBannerShowType(newShowTypes);
                    console.log("Banner Show Type changed:", newShowTypes);
                    // Reset movie and TV show selections when banner show type changes
                    form.setFieldsValue({
                      movie_ids: [],
                      tv_series_show_ids: [],
                    });
                  }}
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={6}>
            <Form.Item
              name="is_auth_required"
              label="Enable Authorization"
              tooltip={{
                title: "Enable authorization for this collection",
                icon: <InfoCircleOutlined />,
              }}
            >
              <ToggleSwitch />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="is_view_all"
              label="Enable View All"
              tooltip={{
                title: "Show a View All link leading to a larger listing page",
                icon: <InfoCircleOutlined />,
              }}
            >
              <ToggleSwitch />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item name="view_all_title" label="View All Title">
              <Input placeholder="e.g. View All" />
            </Form.Item>
          </Col>
          <Col xs={24} md={9}>
            <Form.Item name="view_all_url" label="View All URL">
              <Input placeholder="https://..." />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item
              name="is_unique_series"
              label="Unique Series"
              tooltip={{
                title: "Avoid duplicate series entries across combined sources",
                icon: <InfoCircleOutlined />,
              }}
            >
              <ToggleSwitch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="is_unique_movie"
              label="Unique Movies"
              tooltip={{
                title: "Avoid duplicate movie entries across combined sources",
                icon: <InfoCircleOutlined />,
              }}
            >
              <ToggleSwitch />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={8}>
              <Form.Item name="category_list" label="Categories">
              <Select
                mode="multiple"
                allowClear
                options={catOptions}
                showSearch
                filterOption={false}
                onSearch={fetchCategories}
                placeholder="Select categories"
                disabled={!!tagIds?.length}
                onChange={(value) => handleCategoryOrTagsChange("category", value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="tag_ids" label="Tags">
              <Select
                mode="multiple"
                allowClear
                options={tagOptions}
                showSearch
                filterOption={false}
                onSearch={fetchTags}
                placeholder="Select tags"
                disabled={!!categoryList?.length}
                onChange={(value) => handleCategoryOrTagsChange("tags", value)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="content_type_ids" label="Content Types">
              <Select
                mode="multiple"
                allowClear
                options={ctypeOptions}
                showSearch
                filterOption={false}
                onSearch={fetchContentTypes}
                placeholder="Select content types"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="language_ids" label="Languages">
              <Select
                mode="multiple"
                allowClear
                options={langOptions}
                showSearch
                filterOption={false}
                onSearch={fetchLanguages}
                placeholder="Select languages"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="rating_ids" label="Ratings">
              <Select
                mode="multiple"
                allowClear
                options={ratingOptions}
                showSearch
                filterOption={false}
                onSearch={fetchRatings}
                placeholder="Select ratings"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="genre_ids" label="Genres">
              <Select
                mode="multiple"
                onChange={handleGenreChange}
                allowClear
                options={genreOptions}
                showSearch
                filterOption={false}
                onSearch={fetchGenres}
                placeholder="Select genres"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="sub_genre_ids" label="Sub Genre">
              <Select
                mode="multiple"
                placeholder="Select sub genres"
                loading={loading}
                showSearch
                filterOption={false}
                disabled={!form.getFieldValue("genre_ids")?.length}
                options={subGenres}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="country_ids" label="Countries">
              <Select
                mode="multiple"
                allowClear
                options={countryOptions}
                showSearch
                filterOption={false}
                onSearch={fetchCountries}
                placeholder="Select countries"
              />
            </Form.Item>
          </Col>
          {bannerType === "Show" && bannerShowType.includes("series") && (
            <Col xs={24} md={8}>
              <Form.Item name="tv_series_show_ids" label="TV Shows">
                <Select
                  mode="multiple"
                  allowClear
                  options={tvOptions}
                  showSearch
                  filterOption={false}
                  onSearch={fetchTV}
                  placeholder="Search TV shows"
                />
              </Form.Item>
            </Col>
          )}
          {bannerType === "Show" && bannerShowType.includes("movie") && (
            <Col xs={24} md={8}>
              <Form.Item name="movie_ids" label="Movies">
                <Select
                  mode="multiple"
                  allowClear
                  options={movieOptions}
                  showSearch
                  filterOption={false}
                  onSearch={fetchMovies}
                  placeholder="Search movies"
                />
              </Form.Item>
            </Col>
          )}
          {/* Which announcements the "What's New" rail shows. Only rendered for an
              Announcement collection, and optional within it: left empty the rail
              takes every active announcement, newest first — the behaviour before
              this picker existed, so no existing collection changes. */}
          {collectionType === "Announcement" && (
            <Col xs={24} md={8}>
              <Form.Item
                name="announcement_ids"
                label="Announcements"
                tooltip={{
                  title:
                    "Leave empty to show every active announcement, newest first",
                  icon: <InfoCircleOutlined />,
                }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  options={announcementOptions}
                  showSearch
                  filterOption={false}
                  onSearch={fetchAnnouncements}
                  placeholder="Search announcements"
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Card>

      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: "8px 0",
          borderTop: "1px solid #f0f0f0",
          marginTop: 8,
          zIndex: 1,
        }}
      >
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<SaveOutlined />}
          >
            Save Filters
          </Button>
          {onBack && (
            <Button onClick={onBack} icon={<ArrowLeftOutlined />}>
              Back
            </Button>
          )}
        </Space>
      </div>
    </Form>
  );
};

export default CollectionFiltersForm;
